/**
 * Die Dokumente-Checkliste - Portierung von `renderDocChecklist` (`index.html:12639-12667`), samt
 * der modulinternen Helfer `documentTypeAllowsMultiple` (`:12669-12671`) und `documentIconName`
 * (`:4537-4544`).
 *
 * ================= Vier bewusste Ergaenzungen gegenueber dem woertlichen Markup ================
 *
 * 1. **`role="list"`/`role="listitem"`** auf `.chk-list`/`.chk-row`. `domSignatur` (Task 6/16 ff.)
 *    erfasst laut eigenem Kopfkommentar ausdruecklich WEDER `id` NOCH `aria-*` - und `role` steht
 *    dort in derselben Kategorie ("kein optisches Merkmal"). Die Ergaenzung aendert also nichts an
 *    der Knotenfolge/den Klassen, macht die Liste aber ueber `getByRole("listitem")` auffindbar (wie
 *    im Task-Brief verlangt) - ohne Tag oder Klasse zu aendern.
 * 2. **Ein eigenes `<input type="file">` je hochladbarer Zeile.** Der Bestand loest den Upload ueber
 *    EIN geteiltes, verstecktes `#pdfInput` ausserhalb des Drawers, dessen `accept` `uploadDocFor`
 *    dynamisch umschreibt (`index.html:12673-12683`) - dieses Element lebt in der App-Huelle
 *    (`App.tsx`), die dieser Task nicht anfasst und die es in diesem Baum noch nicht gibt. Jede Zeile
 *    bekommt deshalb ihr EIGENES, per `aria-label` beschriftetes Datei-Feld (`display:none`, per Ref
 *    vom sichtbaren Knopf ausgeloest) - das macht `screen.getByLabelText(t.label)` moeglich, wie es
 *    der Task-Brief fuer den Komponententest verlangt. Diese Zusatzknoten sind NEU gegenueber der
 *    Baseline; der Struktur-Nachweis (`dokumenteTabStruktur.test.tsx`) blendet `input`-Knoten darum
 *    auf beiden Seiten aus - dieselbe Technik wie die Icon-Filterung in Task 9/10/12/16/17/18,
 *    genauso begruendet dort. `best-03` (die Baseline-Bestellung) hat innerhalb von `.drawer-body`
 *    ohnehin kein einziges `<input>` in diesem Reiter - die Filterung veraendert die gefuehrte
 *    Zusicherung also nicht.
 * 3. **`GeerbtHinweis.tsx`** (Sammelsendung, Task 6, "Der Drawer kennzeichnet geerbte Dokumente") -
 *    eine zusaetzliche, konditional gerenderte Fusszeile je Dokumentzeile. Rendert `null`, solange
 *    `x.d.viaShipmentNo` nicht gesetzt ist (jede Baseline-Bestellung ohne Sammelsendung, also der
 *    komplette bisherige Bestand) - fuer diese aendert sich am DOM nichts, `dokumenteTabStruktur.
 *    test.tsx` bleibt unberuehrt. BERICHTIGT (Fixrunde 1, Review-Befund 5): `<GeerbtHinweis>` steht
 *    als GESCHWISTER von `<Dokumentzeile className="chk-file">`, nicht als dessen Kind - `.chk-file`
 *    ist `display:flex` OHNE `flex-wrap` (bestand.css:843), ein Kind haette dort nie umgebrochen,
 *    sondern waere rechts neben dem Papierkorb in derselben Zeile gelandet. `.chk-files` (der
 *    ELTERN-Container) ist `display:flex; flex-direction:column` (bestand.css:842) - ein direktes
 *    Kind DAVON wird automatisch zur eigenen Zeile darunter. Siehe Kopfkommentar `GeerbtHinweis.tsx`
 *    fuer die Begruendung des Namens.
 * 4. **Zustand und Sichtbarkeit vom Server** (Spec 2026-11-03-drawer-checkliste-gleichlauf). Der Bestand urteilt aus den Dateien
 *    (`ok` = mindestens eine Datei, Pflicht = `!t.optional`, Sichtbarkeit = `visibleDocTypes`); die Zeilen urteilen jetzt nach dem
 *    geladenen Eintrag der Server-Checkliste (`checklistenZeile.ts#zeilenUrteil`, Gegenstueck zu `bewerteCheckliste`), damit
 *    Drawer, Board, Banner und Archivierung dasselbe zeigen: ein Beleg an der Ist-Zahlung erfuellt den Zahlungsbeleg, eine
 *    geerbte Datei erfuellt nur, wo der Server sie zaehlt, CMR ist ueber Antwerpen Pflicht. Ohne geladene Checkliste (laedt,
 *    gescheitert, `jtl-<n>` ohne Hub-Zeile) gilt der Rueckfall von vorher. Neu im DOM: unter einer Zahlungsbeleg-Zeile OHNE
 *    eigene Datei steht je Beleg an der Ist-Zahlung eine `.chk-file`-Zeile mit dem Zusatz „an der Zahlung“ (Belege an einer
 *    Ist-Zahlung stehen nicht in `pdfs`/der Dokumentliste, `lib/zahlungsbelege.ts#istBelegeFuer`). Fuer Bestellungen ohne
 *    Ist-Beleg bleibt die Knotenfolge, wie sie war.
 */
import { Fragment, useRef } from "react";
import { visibleDocTypes } from "../../../lib/status.ts";
import { CMR_DOC_TYPE, DOC_TYPES, DOCUMENT_OCR_TYPES } from "../../../config/documents.ts";
import { Dokumentzeile } from "./Dokumentzeile.tsx";
import { GeerbtHinweis } from "./GeerbtHinweis.tsx";
import { BelegKategorieWahl, type BelegKategorieEinstellung } from "./BelegKategorieWahl.tsx";
import { zeilenUrteil } from "./checklistenZeile.ts";
import { useDateiDownloadMitStapel } from "../../../api/useDateiDownload.ts";
import { entfernenRechte, kannEntfernen } from "../../../lib/dokumentEntfernen.ts";
import { ZAHLUNGSBELEG_TYPEN, istBelegeFuer, type Zahlungsbelegtyp } from "../../../lib/zahlungsbelege.ts";
import { NUR_LESEN_TITEL } from "../drawerRechte.ts";
import type { DokumentAnsicht } from "../../../api/dokumente.ts";
import type { ChecklistenEintrag, DsvBeleg, Order, OrderDocument } from "../../../lib/types.ts";

/**
 * `icon(name, className)` (`index.html:4016`) samt der Nachbearbeitung durch `hydrateIcons`
 * (`:4021`) - die gemeinsame Registratur zeichnet das fertige `<svg class="lucide lucide-…">`.
 *
 * Bis `feat/09-spa-ikonen` stand hier eine LOKALE Funktion, die nur den Platzhalter
 * `<i data-lucide="…">` erzeugte. Den hydriert in der SPA niemand (die UMD-Fassung laeuft allein
 * im Baseline-Rundlauf des Playwright-Harness) - es blieb eine leere Flaeche. Siehe `Ikone.tsx`.
 */
import { Ikone } from "../../Ikone.tsx";

/** `index.html:12669-12671`, woertlich: einzig `sonstige` erlaubt mehrere Dateien je Typ. */
function documentTypeAllowsMultiple(type: string): boolean {
  return type === "sonstige";
}

/**
 * `index.html:4537-4544`, woertlich - bis auf die Zahlungsbelege: das Original waehlt das Belegsymbol ueber
 * `type.startsWith("zahlungsbeleg")` (der eine Typ und seine zwei Split-Varianten). Mit dem Anzahlungs- und dem
 * Restzahlungs-/Gesamtzahlungsbeleg (Asana 1220057765558651) bekommen beide Typen dasselbe Symbol `receipt-text`
 * (`ZAHLUNGSBELEG_TYPEN`, `lib/zahlungsbelege.ts`).
 */
function documentIconName(document: OrderDocument | null | undefined): string {
  const name = String((document?.name as string) || (document?.path as string) || "").toLowerCase();
  const type = String(document?.type || "");
  if (ZAHLUNGSBELEG_TYPEN.has(type)) return "receipt-text";
  if (type === "bestellexcel" || /\.(xlsx?|xlsm|ods|csv)$/.test(name)) return "file-spreadsheet";
  if (/\.(png|jpe?g|gif|webp|bmp)$/.test(name)) return "image";
  return "file-text";
}

/**
 * Fix "Excel Download Template funktioniert nicht" (Asana 1218211471530480, 07.09.2026): Wortlaut fuer
 * einen gescheiterten Download - siehe Kopfkommentar `DokumenteTab.tsx#herunterladen` fuer die
 * Root-Cause (rohes `<a href>` kann den Authorization-Header nicht mitschicken, 401).
 */
const DOWNLOAD_FEHLGESCHLAGEN_MELDUNG = "Die Datei konnte nicht heruntergeladen werden.";

export interface ChecklisteProps {
  bestellung: Order;
  /**
   * Der geladene Stand von `GET /api/orders/:id/document-checklist` - durchgereicht aus
   * `DokumenteTab.tsx` (dort der lokale Ladezustand). `null` heisst: noch nicht geladen ODER der
   * Ladeversuch ist gescheitert (`DokumenteTab.tsx#checklisteNeuLaden`) - solange sie LAEDT, urteilen die
   * Zeilen im Rueckfall (Datei = ok, sonst "offen"; `checklistenZeile.ts`); nach einem gescheiterten
   * Laden zeigen die Zeilen stattdessen keinen Zustand (`checklisteGescheitert`). Ist sie geladen, urteilt
   * jede Zeile nach ihrem Eintrag; fehlt der Eintrag zum Typ (`jtl-<n>` ohne Hub-Zeile: `[]`), gilt fuer
   * diese Zeile der Rueckfall.
   */
  checkliste: ChecklistenEintrag[] | null;
  /** Gemeinsame Dokumentansicht; vorhanden hat Vorrang vor dem historischen `pdfs`-Feld. */
  dokumente: DokumentAnsicht[] | null;
  /**
   * Das Laden der Dokumentliste ist gescheitert (Fehleranzeige Paket 2, Task 12): dann gibt es KEINEN Rueckfall auf `o.pdfs` - die
   * Flaeche oben im Reiter sagt, dass die Liste fehlt, und eine Datei aus dem Rueckfall liesse sie vollstaendig aussehen. Solange
   * die Liste noch laedt, bleibt der Rueckfall. Dazu zeigen die Zeilen keinen Zustand (`zustandUnbekannt`).
   */
  dokumenteGescheitert?: boolean;
  /**
   * Das Laden der Checkliste ist gescheitert (Task 12, Ruling R-T12a): der Status je Typ („entfaellt“) ist unbekannt, also zeigen die
   * Zeilen keinen Zustand und bieten kein „manuell abhaken“/„zurücksetzen“ an. Datei-Angaben aus der geladenen Dokumentliste bleiben.
   */
  checklisteGescheitert?: boolean;
  /** `uploadDocFor(type)` gefolgt von der Dateiauswahl (`addDocs`) - hier in EINEM Schritt. */
  onDateiGewaehlt: (typeId: string, datei: File) => void;
  /** `toggleDocManual(type)`/`toggleDocNa(type)` (`index.html:12728-12738`) - dieselbe Funktion. */
  onManuellUmschalten: (typeId: string) => void;
  /**
   * Entfernt die Verknuepfung dieses Dokuments mit der Bestellung (Spec „Dokument entfernen“;
   * `DokumenteTab.tsx#loeschen`). Ersetzt `removeDoc(i)` (`index.html:13002-13014`): statt eines Index
   * in `pdfs` geht die Zeile selbst herein, mit `id` (= `document_links.id`) und `rowVersion`.
   */
  onLoeschen: (dokument: OrderDocument) => void;
  /**
   * Ob der Nutzer Dokumente dieses Typs entfernen darf (Upload-Recht, `lib/dokumentEntfernen.ts`). Ohne
   * Recht bleibt der Muelleimer sichtbar, aber gesperrt (Matrix: Flaeche „gesperrt“). Hier selbst
   * gesetzt statt nur vom Rechtedurchgang des Drawers: der Muelleimer erscheint erst, wenn die Belegliste
   * geladen ist - der Durchgang laeuft aber nur nach einem Zeichnen des DRAWERS, nicht dieses Kindes.
   * `data-permission` am Knopf bleibt trotzdem noetig, damit der Durchgang ihn fuer die Zahlungsrolle
   * (kein `manage_documents`) nicht sperrt. Ohne Angabe: bedienbar.
   */
  darfEntfernen?: (docType: string) => boolean;
  /** Ein Entfernen laeuft gerade (Doppelklick-Schutz, `DokumenteTab.tsx`): alle Muelleimer gesperrt. */
  entfernenGesperrt?: boolean;
  /**
   * Ein Upload laeuft gerade (Task 5 "Uploads bis 100 MB", `DokumenteTab.tsx`): die Datei-Felder und die
   * Knoepfe, die sie oeffnen, sind gesperrt - eine zweite Auswahl liefe sonst neben dem laufenden Upload
   * (und gegen eine noch nicht neu geladene Checkliste, siehe `rowVersionFuer`).
   */
  uploadGesperrt?: boolean;
  /** `extractPiFromDoc`/`extractBusinessDocumentFromDoc` (`index.html:12660-12661`). */
  onAuslesen: (index: number, typeId: string) => void;
  /** Freigabe eines eigenen, nicht positionsbezogenen Dokumentlinks für die aktive Bestellgruppe. */
  onDokumentFreigeben?: (dokument: OrderDocument) => void;
  /** Das Gruppen-Gate lebt im umgebenden Dokumentreiter, der den Gruppenstand kennt. */
  darfFuerGruppeFreigeben?: (dokument: OrderDocument) => boolean;
  /** `doc_type` aus der Adresse (Spezifikation § 3.9, Aufgabe 7) - durchgereicht aus
   *  `DokumenteTab.tsx`, siehe dortiger Kommentar an derselben Prop. */
  hervorgehoben?: string | null;
  /**
   * Die DSV-Belege der Bestellung (`GET .../dsv-belege`) und die Eingaben der Auswahl „Kategorie
   * zuweisen …“ (Spec 2026-10-17). Ein Eintrag des Blocks „Automatische Dokumente“ (`id` =
   * `dsv:<belegId>`) findet darueber seine Belegzeile; ohne beide Angaben gibt es keine Auswahl.
   */
  belege?: DsvBeleg[] | null;
  belegKategorie?: BelegKategorieEinstellung;
}

export function Checkliste({
  bestellung: o,
  checkliste,
  dokumente,
  dokumenteGescheitert,
  checklisteGescheitert,
  onDateiGewaehlt,
  onManuellUmschalten,
  onLoeschen,
  darfEntfernen,
  entfernenGesperrt,
  uploadGesperrt,
  onAuslesen,
  onDokumentFreigeben,
  darfFuerGruppeFreigeben,
  hervorgehoben,
  belege,
  belegKategorie,
}: ChecklisteProps) {
  const eingaben = useRef<Record<string, HTMLInputElement | null>>({});
  const pdfs = (o.pdfs as OrderDocument[] | undefined) || [];
  /** Siehe Kopfkommentar `api/useDateiDownload.ts` fuer die Root-Cause (401-Bug) und die
   * Zusammenführung mit `DokumenteTab.tsx`/`DsvBelege.tsx`/`PaymentTab/IstZahlung.tsx` (bis Ticket H auch
   * `ZahlungTab.tsx`). Die Fehlerzeile am Ende der Checkliste entfällt (Fehleranzeige Paket 1, Spec R2): ein
   * gescheiterter Download geht an den Stapel. */
  const { herunterladen } = useDateiDownloadMitStapel(DOWNLOAD_FEHLGESCHLAGEN_MELDUNG);
  /**
   * Der geladene Checklistenstand nach `docType` - NUR LESESTELLE, keine Schreibstelle. Geschrieben
   * wird ueber `onManuellUmschalten` -> `DokumenteTab.tsx#checklisteStatusUmschalten` -> `PATCH
   * /api/orders/:id/documents/:docType` (dort steht der Root-Cause-Hinweis).
   *
   * Ersetzt seit dem Auftrag "Dokumente manuell abhaken" die vorherige Lesestelle
   * `o.docsManual`/`o.docsNa` - beide Felder wurden von KEINEM Schreibweg mehr befuellt
   * (`docsManual`s einzige Schreibstelle warf synchron `FeldOhneGruppeFehler`, siehe
   * `DokumenteTab.tsx`), der Haken blieb deshalb selbst nach einem (vermeintlich) erfolgreichen Klick
   * unsichtbar - stiller statt lauter Fehlschlag. `o.docsManual`/`o.docsNa` bleiben unangetastet
   * fuer die uebrigen Lesestellen dieses Auftrags-Randes (`lib/status.ts#docProgress`,
   * `PaymentTab/ZahlungTab.tsx#arbeitskopie`) - andere Zustaendigkeit, siehe Auftrag "Nicht anfassen". Der fruehere
   * Schreiber der Belegtypen beim Zahlungsart-Wechsel (Altbestand) ist mit dem Anzahlungsbeleg entfallen.
   */
  const eintragNachTyp = new Map<string, ChecklistenEintrag>((checkliste ?? []).map((e) => [e.docType, e]));
  const automatischeDokumente = (dokumente ?? []).filter((d) => d.docType === null);
  /**
   * Fehleranzeige Paket 2, Task 12 (Spec E5 „Die Bereiche ohne Daten zeigen nichts“, Ruling R-T12a): fehlt die Checkliste ODER die
   * Dokumentliste (Laden gescheitert), kennt der Reiter den Zustand der Typen nicht - der leere Kreis „offen“ behauptete „nichts
   * da“ (mit „manuell abhaken“), wo vielleicht alles da ist. Dann zeigen die Zeilen weder Symbol noch Klassen `ok`/`na` noch die
   * Zustandsknoepfe. Zeilenname, Datei-Angaben aus einer geladenen Dokumentliste und das HOCHLADEN samt Sperren bleiben (E14).
   */
  const zustandUnbekannt = !!dokumenteGescheitert || !!checklisteGescheitert;
  /** Die Typen, die der Drawer bisher zeigte - Rueckfall der Sichtbarkeit (`checklistenZeile.ts`): daraus verschwindet keine Zeile. */
  const bisherSichtbar = new Set(visibleDocTypes(o).map((t) => t.id));

  return (
    <>
    <div className="chk-list" role="list">
      {[...DOC_TYPES, CMR_DOC_TYPE].map((t) => {
        const dateien: Array<{ d: OrderDocument; i: number }> = dokumente
          ? dokumente.filter((d) => d.docType === t.id && d.fileId !== null).map((d) => ({
            d: { id: d.id, type: d.docType ?? undefined, name: d.originalName,
              path: d.downloadPath ?? undefined, source: d.source, rowVersion: d.rowVersion,
              viaGroupId: d.viaGroupId, viaShipmentNo: d.viaShipmentNo, geteiltMit: d.geteiltMit ?? undefined,
              groupLabel: d.groupLabel, herkunft: d.herkunft, reichweiteAnzahl: d.reichweiteAnzahl }, i: -1,
          }))
          : dokumenteGescheitert
          ? []
          : pdfs
          .map((d, i) => ({ d, i }))
          .filter((x) => (x.d.type || "sonstige") === t.id);
        // Zustand, Pflicht und Sichtbarkeit: nach dem Eintrag der Server-Checkliste, im Rueckfall nach den Dateien
        // (`checklistenZeile.ts`, Kopfkommentar Ergaenzung 4). "entfaellt" ist der einzige manuelle Zustand (siehe Kopfkommentar
        // `checklisteStatusUmschalten` in `DokumenteTab.tsx`); "vorhanden" setzt allein der Server.
        const urteil = zeilenUrteil({
          typ: t,
          eintrag: eintragNachTyp.get(t.id),
          checklisteGeladen: checkliste !== null,
          zustandUnbekannt,
          dateien: dateien.length,
          rueckfallSichtbar: bisherSichtbar.has(t.id),
        });
        if (!urteil.sichtbar) return null;
        const { zustand, pflicht } = urteil;
        const unbekannt = zustand === "unbekannt";
        const ok = zustand === "ok";
        const manual = zustand === "na";
        const hatDateien = dateien.length > 0;
        const mehrfach = documentTypeAllowsMultiple(t.id);
        /** Belege an der Ist-Zahlung (nicht in `pdfs`/der Dokumentliste): nur unter einer Zeile ohne eigene Datei. */
        const istBelege = ZAHLUNGSBELEG_TYPEN.has(t.id) && !hatDateien ? istBelegeFuer(o, t.id as Zahlungsbelegtyp) : [];

        return (
          <div
            key={t.id}
            role="listitem"
            className={[
              "chk-row",
              ok ? "ok" : "",
              manual ? "na" : "",
            ].filter(Boolean).join(" ")}
          >
            <div className="chk-head">
              {/* Der Rahmen bleibt im Baum, damit die Zeilennamen ihre Spalte behalten - aber UNSICHTBAR (Ruling R-T12c):
                  `bestand.css` zeichnet `.chk-state` allein schon als leeres weisses Kaestchen, und das ist das Bild einer
                  OFFENEN Zeile (das Symbol `circle` ist dort `currentColor` = transparent). Inline, weil der Test sonst kein
                  Stylesheet sieht. `bestand.css` bleibt unberuehrt (an `index.html` gebunden). */}
              <div
                className="chk-state"
                style={unbekannt ? { visibility: "hidden" } : undefined}
                aria-hidden={unbekannt ? true : undefined}
              >
                {unbekannt ? null : <Ikone name={ok || manual ? "circle-check" : "circle"} />}
              </div>
              <div className="chk-label">
                {t.label}
                {!pflicht ? <span className="doc-optional">optional</span> : null}
                {manual ? (
                  <span style={{ fontWeight: 400, color: "var(--ink-faint)" }}>
                    {" "}
                    ({pflicht ? "manuell abgehakt" : "nicht notwendig"})
                  </span>
                ) : null}
              </div>
              <div className="chk-actions">
                {!t.manualOnly ? (
                  <>
                    <button
                      type="button"
                      className="chk-btn"
                      disabled={!!uploadGesperrt}
                      onClick={() => eingaben.current[t.id]?.click()}
                    >
                      <Ikone name={hatDateien && !mehrfach ? "refresh-cw" : "upload"} />
                      {hatDateien && !mehrfach ? "Überschreiben" : "Hochladen"}
                    </button>
                    <input
                      type="file"
                      aria-label={t.label}
                      disabled={!!uploadGesperrt}
                      style={{ display: "none" }}
                      ref={(el) => {
                        eingaben.current[t.id] = el;
                      }}
                      onChange={(e) => {
                        const datei = e.currentTarget.files?.[0];
                        e.currentTarget.value = "";
                        if (datei) onDateiGewaehlt(t.id, datei);
                      }}
                    />
                  </>
                ) : null}
                {zustand === "offen" || manual ? (
                  <button
                    type="button"
                    className={`chk-btn${manual ? " na-active" : ""}`}
                    title={
                      !pflicht
                        ? `${t.label} ist für diese Bestellung nicht notwendig`
                        : "Ohne Upload als erledigt markieren"
                    }
                    onClick={() => onManuellUmschalten(t.id)}
                  >
                    <Ikone name={manual ? "refresh-cw" : !pflicht ? "circle-minus" : "circle-check"} />
                    {manual ? "zurücksetzen" : !pflicht ? "Nicht notwendig" : "manuell abhaken"}
                  </button>
                ) : null}
              </div>
            </div>
            {hatDateien || istBelege.length ? (
              <div className="chk-files">
                {dateien.map((x) => {
                  const dateiname = String((x.d.name as string) || (x.d.path as string) || "");
                  const istPdf = /\.pdf$/i.test(dateiname);
                  const zeigeAuslesen =
                    istPdf && (t.id === "pi" || !!DOCUMENT_OCR_TYPES[t.id as string]);
                  return (
                    <Fragment key={typeof x.d.id === "string" ? x.d.id : x.i}>
                      <Dokumentzeile
                        docType={x.d.type}
                        hervorgehoben={hervorgehoben}
                        className="chk-file"
                      >
                        <a
                          className="icon-label"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            void herunterladen(x.d.path, x.d.name as string);
                          }}
                        >
                          <Ikone name={documentIconName(x.d)} />
                          {x.d.name as string}
                        </a>
                        {x.d.source ? <span className="doc-overwrite-note">
                          {x.d.source === "sharepoint_import" ? "SharePoint-Import" : "Hochgeladen"}
                        </span> : null}
                        {zeigeAuslesen ? (
                          <button
                            type="button"
                            className="chk-btn"
                            onClick={() => onAuslesen(x.i, t.id)}
                          >
                            <Ikone name="scan-text" />
                            Auslesen
                          </button>
                        ) : null}
                        {darfFuerGruppeFreigeben?.(x.d) ? (
                          <button type="button" className="btn-ghost" onClick={() => onDokumentFreigeben?.(x.d)}>
                            Für Bestellgruppe freigeben
                          </button>
                        ) : null}
                        {kannEntfernen(x.d, x.i) ? <button
                          type="button"
                          className="row-del"
                          title={darfEntfernen && !darfEntfernen(t.id) ? NUR_LESEN_TITEL : "Dokument entfernen"}
                          disabled={(darfEntfernen ? !darfEntfernen(t.id) : false) || !!entfernenGesperrt}
                          data-permission={entfernenRechte(t.id).join(" ")}
                          onClick={() => onLoeschen(x.d)}
                        >
                          <Ikone name="trash-2" />
                        </button> : null}
                        {x.d.overwriteNote ? (
                          <span className="doc-overwrite-note">
                            Überschrieben: {x.d.overwriteNote as string}
                          </span>
                        ) : null}
                      </Dokumentzeile>
                      <GeerbtHinweis dokument={x.d} />
                    </Fragment>
                  );
                })}
                {istBelege.map((beleg, i) => (
                  <Dokumentzeile key={`ist-${i}-${beleg.documentId}`} docType={t.id} className="chk-file">
                    <a
                      className="icon-label"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        void herunterladen(beleg.path, beleg.name);
                      }}
                    >
                      <Ikone name="receipt-text" />
                      {beleg.name}
                    </a>
                    <span className="doc-overwrite-note">an der Zahlung</span>
                  </Dokumentzeile>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
    {automatischeDokumente.length ? (
      <div className="chk-list" role="list" aria-label="Automatische Dokumente">
        <div className="chk-row ok" role="listitem">
          <div className="chk-head"><div className="chk-label">Automatische Dokumente</div></div>
          <div className="chk-files">
            {automatischeDokumente.map((dokument) => {
              const beleg = belege?.find((b) => `dsv:${b.id}` === dokument.id);
              return (
              <Dokumentzeile key={dokument.id} docType={null} className="chk-file">
                {dokument.downloadPath ? <a
                  className="icon-label"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    void herunterladen(dokument.downloadPath, dokument.originalName);
                  }}
                >
                  <Ikone name={documentIconName({ name: dokument.originalName })} />{dokument.originalName}
                </a> : <button type="button" className="icon-label" disabled title="Dokument nicht verfügbar">
                  <Ikone name={documentIconName({ name: dokument.originalName })} />{dokument.originalName}
                </button>}
                <span className="doc-overwrite-note">DSV</span>
                {beleg && belegKategorie && beleg.dateiVerfuegbar ? (
                  <BelegKategorieWahl
                    bestellungId={o.id as string}
                    belegId={beleg.id}
                    dateiname={beleg.dateiname}
                    aktuellerTyp={beleg.aktuellerHubTyp}
                    {...belegKategorie}
                  />
                ) : null}
              </Dokumentzeile>
              );
            })}
          </div>
        </div>
      </div>
    ) : null}
    </>
  );
}
